import React from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { AuthenticatedTemplate } from '@azure/msal-react';
import {
    Landing,
    ServerErrorPage,
    PageNotFound,
    Notification,
    EditProfile,
} from './components/shared';
import {
    Login,
    Signup,
    ForgetPassword,
    HiringClientListing,
    DocumentListing as DocumentListingCustomer,
    CustomerDashboard,
    ScoreCardReport,
    ResetPassword,
    DigitalSafetyLibraryCustomer,
} from './components/Customer';
import {
    SSOLogin,
    AdminDashboard,
    DocumentCategoryListing,
    CsmListing,
    CustomerListing,
    DocumentListing,
    AdminListing,
    ScoreCardReportAdmin,
    NewsFeed,
    AdminScoreCardListing,
    DocumentRequestView,
    RegulatoryIssue,
    EditCustomer,
    CSMHiringClientOrder,
    DigitalSafetyLibrary,
} from './components/Admin';
import { LOCAL_STORAGE, ROUTES } from './constants';
import { getLocalStorageItem } from './utils';
import LegendPage from './components/shared/common/LegendPage';
import SafetyMeetings from './components/Admin/SafetyMeetings';
import { AllNewsFeedCustomer } from './components/Customer/NewsFeed/AllNewsFeedCustomer';
import { SalesAgentListing } from './components/Admin/SalesAgentListing/SalesAgentListing';
import { ContentManagerList } from './components/Admin/ContentManagerListing/ContentManagerList';
import { DigitalLaborLawPosters } from './components/Admin/DigitalLaborLawPosters/DigitalLaborLawPosters';
import { DigitalLaborLawPostersCustomer } from './components/Customer/DigitalLaborLawPostersCustomer/DigitalLaborLawPostersCustomer';
import { FoldersView } from './components/Customer/MyDocuments/FoldersView';
import { DocumentsView } from './components/Customer/MyDocuments/DocumentsView';
import AccountReview from './components/Admin/AccountReview';
import AccountReviewPage from './components/Admin/AccountReview/AccountReviewPage';
import { ScorecardLandingPage } from './components/Customer/ScoreCardReport/ScorecardLandingPage';
import AccountsListing from './components/Customer/AccountsListing';

const AdminAuthGuard = () => {
    const userRole = getLocalStorageItem(LOCAL_STORAGE.ROLE_CODE);
    return userRole === null ? <SSOLogin /> : <Navigate to={ROUTES.ADMIN.DEFAULT} replace />;
};
const CustomerAuthGuard = () => {
    const userRole = getLocalStorageItem(LOCAL_STORAGE.ROLE_CODE);
    return userRole === null ? <Login /> : <Navigate to={ROUTES.CUSTOMER.DEFAULT} replace />;
};

const adminRouter = createBrowserRouter([
    {
        path: ROUTES.HOME,
        element: <Landing />,
        children: [
            {
                path: ROUTES.ADMIN.LOGIN,
                element: <AdminAuthGuard />,
            },
            {
                path: ROUTES.EMPTY,
                element: <AdminAuthGuard />,
            },
        ],
    },
    {
        path: ROUTES.ADMIN.DEFAULT,
        element: <AdminDashboard />,
        children: [
            {
                path: ROUTES.ADMIN.DASHBOARD,
                element: (
                    <AuthenticatedTemplate>
                        <AdminDashboard />
                    </AuthenticatedTemplate>
                ),
            },
            {
                path: ROUTES.ADMIN.DOCUMENT_CATEGORY,
                element: <DocumentCategoryListing />,
            },
            {
                path: ROUTES.ADMIN.DOCUMENTS,
                element: <DocumentListing />,
            },
            {
                path: ROUTES.ADMIN.ADMIN,
                element: <AdminListing />,
            },
            {
                path: ROUTES.ADMIN.CSM,
                element: <CsmListing />,
            },
            {
                path: ROUTES.ADMIN.CUSTOMER,
                element: <CustomerListing />,
            },
            {
                path: ROUTES.ADMIN.CUSTOMER_EDIT,
                element: <EditCustomer />,
            },
            {
                path: ROUTES.ADMIN.DOCUMENT_REQUEST,
                element: <DocumentRequestView />,
            },
            {
                path: ROUTES.ADMIN.SCORE_CARD_REPORT_ADMIN,
                element: <ScoreCardReportAdmin />,
            },
            {
                path: ROUTES.ADMIN.NEWS_FEED_ADMIN,
                element: <NewsFeed />,
            },
            {
                path: ROUTES.ADMIN.NOTIFICATION,
                element: <Notification />,
            },
            {
                path: ROUTES.ADMIN.ADMIN_SCORE_CARD_LISTING,
                element: <AdminScoreCardListing />,
            },
            {
                path: ROUTES.ADMIN.EDIT_PROFILE,
                element: <EditProfile />,
            },
            {
                path: ROUTES.ADMIN.ADMIN_REGULATORY_ISSUE,
                element: <RegulatoryIssue />,
            },
            {
                path: ROUTES.ADMIN.CUSTOMER_HIRING_CLIENT_ORDER,
                element: <CSMHiringClientOrder />,
            },
            {
                path: ROUTES.ADMIN.DIGITAL_SAFETY_LIBRARY,
                element: <DigitalSafetyLibrary />,
            },
            {
                path: ROUTES.ADMIN.LEGEND,
                element: <LegendPage />,
            },
            {
                path: ROUTES.ADMIN.SAFETY_MEETINGS,
                element: <SafetyMeetings />,
            },
            {
                path: ROUTES.ADMIN.SALES_AGENT,
                element: <SalesAgentListing />,
            },
            {
                path: ROUTES.ADMIN.CONTENT_MANAGER,
                element: <ContentManagerList />,
            },
            {
                path: ROUTES.ADMIN.DIGITAL_LABOR_POSTERS,
                element: <DigitalLaborLawPosters />,
            },
            {
                path: ROUTES.ADMIN.ACCOUNT_REVIEWS,
                element: <AccountReview />,
            },
            {
                path: ROUTES.ADMIN.ACCOUNT_REVIEW_PAGE,
                element: <AccountReviewPage />,
            },
        ],
    },
    {
        path: ROUTES.SERVER_ERROR,
        element: <ServerErrorPage />,
    },
    {
        path: '*',
        element: <PageNotFound />,
    },
]);
const customerRouter = createBrowserRouter([
    {
        path: ROUTES.HOME,
        element: <Landing />,
        children: [
            {
                path: ROUTES.CUSTOMER.LOGIN,
                element: <CustomerAuthGuard />,
            },
            {
                path: ROUTES.EMPTY,
                element: <CustomerAuthGuard />,
            },
            {
                path: ROUTES.CUSTOMER.SIGNUP,
                element: <Signup />,
            },
            {
                path: ROUTES.CUSTOMER.FORGOT_PASSWORD,
                element: <ForgetPassword />,
            },
            {
                path: ROUTES.CUSTOMER.RESET_PASSWORD,
                element: <ResetPassword />,
            },
        ],
    },
    {
        path: ROUTES.CUSTOMER.DEFAULT,
        element: <CustomerDashboard />,
        children: [
            {
                path: ROUTES.CUSTOMER.DASHBOARD,
                element: <CustomerDashboard />,
            },
            {
                path: ROUTES.CUSTOMER.DOCUMENTS,
                element: <DocumentListingCustomer />,
            },
            {
                path: ROUTES.CUSTOMER.SCORE_CARD_REPORT,
                element: <ScoreCardReport />,
            },
            {
                path: ROUTES.CUSTOMER.SCORE_CARD_LANDING_PAGE,
                element: <ScorecardLandingPage />,
            },
            {
                path: ROUTES.CUSTOMER.ACCOUNTS,
                element: <AccountsListing />,
            },
            {
                path: ROUTES.CUSTOMER.HIRING_CLIENTS,
                element: <HiringClientListing />,
            },
            {
                path: ROUTES.CUSTOMER.NOTIFICATION,
                element: <Notification />,
            },
            {
                path: ROUTES.CUSTOMER.EDIT_PROFILE,
                element: <EditProfile />,
            },
            {
                path: ROUTES.CUSTOMER.DIGITAL_SAFETY_LIBRARY,
                element: <DigitalSafetyLibraryCustomer />,
            },
            {
                path: ROUTES.CUSTOMER.LEGEND,
                element: <LegendPage />,
            },
            {
                path: ROUTES.CUSTOMER.SAFETY_MEETINGS,
                element: <SafetyMeetings />,
            },
            {
                path: ROUTES.CUSTOMER.NEWS_FEEDS,
                element: <AllNewsFeedCustomer />,
            },
            {
                path: ROUTES.CUSTOMER.DIGITAL_LABOR_POSTERS,
                element: <DigitalLaborLawPostersCustomer />,
            },
            {
                path: ROUTES.CUSTOMER.MY_FOLDERS_VIEW,
                element: <FoldersView />,
            },
            {
                path: ROUTES.CUSTOMER.MY_DOCUMENTS_VIEW,
                element: <DocumentsView />,
            },
            {
                path: ROUTES.CUSTOMER.ACCOUNT_REVIEWS,
                element: <AccountReview />,
            },
            {
                path: ROUTES.CUSTOMER.ACCOUNT_REVIEW_PAGE,
                element: <AccountReviewPage />,
            },
        ],
    },
    {
        path: ROUTES.SERVER_ERROR,
        element: <ServerErrorPage />,
    },
    {
        path: '*',
        element: <PageNotFound />,
    },
]);

const getRouter = () => {
    const isSubdomainAdmin = window.location.hostname.startsWith(
        // eslint-disable-next-line no-undef
        process.env.REACT_APP_ADMIN_DOMAIN,
    );
    return isSubdomainAdmin ? adminRouter : customerRouter;
};

export const router = getRouter();

export default function AppRouter() {
    return <RouterProvider router={router} />;
}
